import React, { useEffect, useState, useRef } from 'react';
import { Target, Heart, TrendingUp, Users, Sparkles } from 'lucide-react';
import { motion, useInView } from 'framer-motion';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';

const CountUpAnimation = ({ end, duration = 2, suffix = '', prefix = '' }) => {
  const [count, setCount] = useState(0);
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true });

  useEffect(() => {
    if (!isInView) return;

    let startTime;
    let animationFrame;

    const animate = (currentTime) => {
      if (!startTime) startTime = currentTime;
      const progress = Math.min((currentTime - startTime) / (duration * 1000), 1);

      // Easing function for smooth animation
      const easeOutQuart = 1 - Math.pow(1 - progress, 4);
      const currentCount = Math.floor(easeOutQuart * end);

      setCount(currentCount);

      if (progress < 1) {
        animationFrame = requestAnimationFrame(animate);
      } else {
        setCount(end);
      }
    };

    animationFrame = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(animationFrame);
  }, [isInView, end, duration]);

  return (
    <span ref={ref}>
      {prefix}{count}{suffix}
    </span>
  );
};

const About = () => {
  const values = [
    {
      icon: Target,
      title: "Mission-Driven",
      description: "We're committed to delivering innovative solutions that solve real-world problems and drive business growth.",
      gradient: 'from-blue-500 to-cyan-500'
    },
    {
      icon: Heart,
      title: "Client-Focused",
      description: "Your success is our success. We build lasting partnerships through trust, transparency, and exceptional service.",
      gradient: 'from-rose-500 to-pink-500'
    },
    {
      icon: TrendingUp,
      title: "Continuous Learning",
      description: "We stay ahead of technology trends, constantly learning and adapting to provide cutting-edge solutions.",
      gradient: 'from-purple-500 to-violet-500'
    },
    {
      icon: Users,
      title: "Collaborative Approach",
      description: "We work closely with you throughout the journey, ensuring your vision is brought to life perfectly.",
      gradient: 'from-indigo-500 to-blue-500'
    }
  ];

  return (
    <section id="about" className="py-24 bg-white dark:bg-gray-900 relative overflow-hidden">
      {/* Background decoration */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/4 -left-48 w-96 h-96 bg-gradient-to-br from-blue-100 via-purple-100 to-pink-100 dark:from-blue-900/10 dark:via-purple-900/10 dark:to-pink-900/10 rounded-full filter blur-3xl opacity-40"></div>
        <div className="absolute bottom-1/4 -right-48 w-96 h-96 bg-gradient-to-tl from-cyan-100 via-blue-100 to-purple-100 dark:from-cyan-900/10 dark:via-blue-900/10 dark:to-purple-900/10 rounded-full filter blur-3xl opacity-40"></div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        {/* Section Header */}
        <motion.div 
          className="text-center mb-20"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <motion.div
            className="inline-flex items-center space-x-2 bg-white dark:bg-gray-800 px-5 py-2.5 rounded-full mb-6 border-2 border-blue-200 dark:border-blue-800 shadow-lg"
            initial={{ scale: 0.9, opacity: 0 }}
            whileInView={{ scale: 1, opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <Sparkles className="w-4 h-4 text-blue-600 dark:text-blue-400" />
            <span className="text-sm font-semibold text-gray-700 dark:text-gray-300">Our Story</span>
          </motion.div>

          <motion.h2 
            className="text-5xl md:text-6xl font-bold mb-6"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.1 }}
          >
            <span className="bg-gradient-to-r from-gray-900 via-blue-800 to-purple-800 dark:from-white dark:via-blue-400 dark:to-purple-400 bg-clip-text text-transparent">
              About Code Mitra
            </span>
          </motion.h2>
          
          <motion.p 
            className="text-xl md:text-2xl text-gray-600 dark:text-gray-400 max-w-3xl mx-auto leading-relaxed"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            Your trusted technology partner,{' '}
            <span className="font-semibold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
              dedicated to building exceptional digital solutions
            </span>
          </motion.p>
        </motion.div>

        {/* Main Content Cards */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 mb-20">
          {/* Who We Are Card */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.1 }}
          >
            <motion.div
              whileHover={{ y: -10, scale: 1.02 }}
              transition={{ duration: 0.3 }}
            >
              <Card className="h-full bg-white dark:bg-gray-800 border-2 border-gray-100 dark:border-gray-700 shadow-2xl hover:shadow-3xl rounded-3xl overflow-hidden relative group">
                {/* Gradient accent bar */}
                <div className="absolute top-0 left-0 right-0 h-1.5 bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500"></div>
                
                {/* Hover glow effect */}
                <div className="absolute inset-0 bg-gradient-to-br from-blue-50/50 to-purple-50/50 dark:from-blue-900/10 dark:to-purple-900/10 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                
                <CardContent className="p-10 relative z-10">
                  <div className="flex items-center space-x-3 mb-6">
                    <motion.div 
                      className="w-12 h-12 bg-gradient-to-br from-blue-500 to-purple-500 rounded-2xl flex items-center justify-center shadow-lg"
                      whileHover={{ rotate: 360 }}
                      transition={{ duration: 0.6 }}
                    >
                      <Users className="w-6 h-6 text-white" />
                    </motion.div>
                    <h3 className="text-3xl font-bold bg-gradient-to-r from-gray-900 to-blue-900 dark:from-white dark:to-blue-400 bg-clip-text text-transparent">
                      Who We Are
                    </h3>
                  </div>
                  
                  <div className="space-y-5 text-gray-700 dark:text-gray-300">
                    <p className="text-lg leading-relaxed">
                      Code Mitra is more than just a tech company –{' '}
                      <span className="font-semibold text-gray-900 dark:text-white">
                        we're your friendly partner in the digital world.
                      </span>{' '}
                      Founded by passionate developers and designers, we believe in the power of technology to transform businesses and lives.
                    </p>
                    <p className="text-lg leading-relaxed">
                      Our team combines{' '}
                      <span className="font-semibold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                        technical expertise with creative thinking
                      </span>{' '}
                      to deliver solutions that are not just functional, but exceptional.
                    </p>
                    <p className="text-lg leading-relaxed">
                      Whether you're a startup with a big idea or an established business looking to innovate,{' '}
                      <span className="font-semibold text-gray-900 dark:text-white">
                        we're here to help you navigate the digital landscape with confidence.
                      </span>
                    </p>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          </motion.div>

          {/* Our Vision Card */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <motion.div
              whileHover={{ y: -10, scale: 1.02 }}
              transition={{ duration: 0.3 }}
            >
              <Card className="h-full bg-white dark:bg-gray-800 border-2 border-gray-100 dark:border-gray-700 shadow-2xl hover:shadow-3xl rounded-3xl overflow-hidden relative group">
                {/* Gradient accent bar */}
                <div className="absolute top-0 left-0 right-0 h-1.5 bg-gradient-to-r from-purple-500 via-pink-500 to-rose-500"></div>
                
                {/* Hover glow effect */}
                <div className="absolute inset-0 bg-gradient-to-br from-purple-50/50 to-pink-50/50 dark:from-purple-900/10 dark:to-pink-900/10 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                
                <CardContent className="p-10 relative z-10">
                  <div className="flex items-center space-x-3 mb-6">
                    <motion.div 
                      className="w-12 h-12 bg-gradient-to-br from-purple-500 to-pink-500 rounded-2xl flex items-center justify-center shadow-lg"
                      whileHover={{ rotate: 360 }}
                      transition={{ duration: 0.6 }}
                    >
                      <Target className="w-6 h-6 text-white" />
                    </motion.div>
                    <h3 className="text-3xl font-bold bg-gradient-to-r from-gray-900 to-purple-900 dark:from-white dark:to-purple-400 bg-clip-text text-transparent">
                      Our Vision
                    </h3>
                  </div>
                  
                  <div className="space-y-6">
                    <p className="text-lg text-gray-700 dark:text-gray-300 leading-relaxed">
                      To empower businesses with technology that makes a difference. We envision a world where{' '}
                      <span className="font-semibold bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">
                        quality tech solutions are accessible to everyone,
                      </span>{' '}
                      regardless of size or budget.
                    </p>
                    
                    {/* Enhanced Quote Box */}
                    <motion.div 
                      className="relative bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50 dark:from-blue-900/20 dark:via-purple-900/20 dark:to-pink-900/20 border-l-4 border-gradient p-8 rounded-2xl shadow-lg"
                      style={{ borderImage: 'linear-gradient(to bottom, #3b82f6, #a855f7, #ec4899) 1' }}
                      whileHover={{ x: 10, scale: 1.02 }}
                      transition={{ duration: 0.3 }}
                    >
                      {/* Glow effect */}
                      <div className="absolute inset-0 bg-gradient-to-r from-blue-500/10 to-purple-500/10 rounded-2xl blur-xl"></div>
                      
                      <div className="relative">
                        <div className="text-6xl text-blue-600 dark:text-blue-400 mb-3 leading-none">"</div>
                        <p className="text-xl font-medium text-gray-800 dark:text-gray-200 italic leading-relaxed">
                          We don't just write code –{' '}
                          <span className="font-bold bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600 bg-clip-text text-transparent">
                            we craft experiences, solve problems, and build digital products that people love to use.
                          </span>
                        </p>
                      </div>
                    </motion.div>

                    {/* Stats Grid */}
                    <div className="grid grid-cols-2 gap-6 pt-6">
                      <motion.div 
                        className="text-center bg-gradient-to-br from-blue-50 to-cyan-50 dark:from-blue-900/30 dark:to-cyan-900/30 backdrop-blur-lg rounded-2xl p-6 shadow-lg border border-blue-100 dark:border-blue-800"
                        initial={{ opacity: 0, scale: 0.8 }}
                        whileInView={{ opacity: 1, scale: 1 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.5, delay: 0.3 }}
                        whileHover={{ scale: 1.1, y: -8 }}
                      >
                        <div className="text-5xl font-bold bg-gradient-to-r from-blue-600 to-cyan-600 bg-clip-text text-transparent mb-2">
                          <CountUpAnimation end={50} suffix="+" />
                        </div>
                        <div className="text-sm font-medium text-gray-600 dark:text-gray-400">
                          Projects Completed
                        </div>
                      </motion.div>

                      <motion.div 
                        className="text-center bg-gradient-to-br from-purple-50 to-pink-50 dark:from-purple-900/30 dark:to-pink-900/30 backdrop-blur-lg rounded-2xl p-6 shadow-lg border border-purple-100 dark:border-purple-800"
                        initial={{ opacity: 0, scale: 0.8 }}
                        whileInView={{ opacity: 1, scale: 1 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.5, delay: 0.4 }}
                        whileHover={{ scale: 1.1, y: -8 }}
                      >
                        <div className="text-5xl font-bold bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent mb-2">
                          <CountUpAnimation end={100} suffix="%" />
                        </div>
                        <div className="text-sm font-medium text-gray-600 dark:text-gray-400">
                          Client Satisfaction
                        </div>
                      </motion.div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          </motion.div>
        </div>

        {/* Values Grid */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.3 }}
        >
          <div className="text-center mb-12">
            <h3 className="text-3xl md:text-4xl font-bold bg-gradient-to-r from-gray-900 via-blue-800 to-purple-800 dark:from-white dark:via-blue-400 dark:to-purple-400 bg-clip-text text-transparent mb-4">
              Our Core Values
            </h3>
            <p className="text-lg text-gray-600 dark:text-gray-400">
              The principles that guide everything we do
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {values.map((value, index) => {
              const IconComponent = value.icon;
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 40 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: 0.1 * index }}
                >
                  <motion.div
                    whileHover={{ y: -12, scale: 1.05 }}
                    transition={{ duration: 0.3 }}
                  >
                    <Card className="h-full bg-white dark:bg-gray-800 border-2 border-gray-100 dark:border-gray-700 shadow-xl hover:shadow-2xl rounded-2xl overflow-hidden group relative">
                      {/* Hover gradient overlay */}
                      <div className={`absolute inset-0 bg-gradient-to-br ${value.gradient} opacity-0 group-hover:opacity-5 transition-opacity duration-500`}></div>
                      
                      <CardHeader className="text-center relative z-10">
                        <motion.div 
                          className={`w-16 h-16 bg-gradient-to-br ${value.gradient} rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg group-hover:shadow-xl`}
                          whileHover={{ 
                            rotate: [0, -15, 15, -15, 0],
                            scale: 1.15
                          }}
                          transition={{ duration: 0.6 }}
                        >
                          <IconComponent className="w-8 h-8 text-white" />
                        </motion.div>
                        <CardTitle className="text-xl font-bold text-gray-900 dark:text-white mb-3">
                          {value.title}
                        </CardTitle>
                      </CardHeader>
                      
                      <CardContent className="px-6 pb-6 text-center relative z-10">
                        <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
                          {value.description}
                        </p>
                      </CardContent>
                    </Card>
                  </motion.div>
                </motion.div>
              );
            })}
          </div>
        </motion.div>

        {/* Bottom CTA */}
        <motion.div
          className="mt-20 text-center"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.4 }}
        >
          <motion.div
            className="inline-block bg-gradient-to-r from-blue-50 via-purple-50 to-pink-50 dark:from-blue-900/20 dark:via-purple-900/20 dark:to-pink-900/20 rounded-3xl p-10 border-2 border-blue-200/50 dark:border-blue-800/50 shadow-2xl"
            whileHover={{ scale: 1.02 }}
            transition={{ duration: 0.3 }}
          >
            <p className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
              Ready to start your digital journey?
            </p>
            <p className="text-lg text-gray-600 dark:text-gray-400 mb-6">
              Let's build something amazing together
            </p>
            <motion.button
              onClick={() => {
                const element = document.getElementById('contact');
                if (element) element.scrollIntoView({ behavior: 'smooth' });
              }}
              className="px-8 py-4 bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600 hover:from-blue-700 hover:via-purple-700 hover:to-pink-700 text-white font-semibold rounded-xl shadow-xl hover:shadow-2xl transition-all duration-300"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              Let's Talk
            </motion.button>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

export default About;
